import { ChipVariant } from "Components/atoms/chip"

// Mirrors api/src/services/project-health/engine.ts and api/src/models/project-health.ts

export enum HealthStatus {
    GOOD = "GOOD",
    WARNING = "WARNING",
    CRITICAL = "CRITICAL",
    NOT_EVALUATED = "NOT_EVALUATED",
}

export enum HealthCheckGroup {
    SETUP = "SETUP",
    FINANCIAL = "FINANCIAL",
}

export interface HealthCheckResult {
    key: string
    group: HealthCheckGroup
    label: string
    status: HealthStatus
    value: number | string | null
    threshold: string
    message: string
    note?: string
}

export type FailingCheck = Pick<HealthCheckResult, "key" | "label" | "status" | "message">

export interface ProjectHealthPeriod {
    fiscalPeriod: number
    planned: number
    actual: number
    forecast: number
    projected: number
    cumulativePlanned: number
    cumulativeActual: number
    cumulativeProjected: number
    hours: number
    billableHours: number
}

export interface ProjectHealth {
    projectId: string
    projectStatus: string | null
    evaluated: boolean
    status: HealthStatus | null
    checks: HealthCheckResult[]
    failingChecks: HealthCheckResult[]
    currentPeriod: number
    computedAt: string
    // Thresholds in effect (Project Health rules + the fiscal year margin target)
    config?: Record<string, number>
    kpis: {
        revenue: number
        billed: number
        accrued: number
        cost: number
        marginPercent: number | null
        marginTarget: number
        marginMinimum: number
        marginWarning: number
        marginFiscalYear: number
        marginSource: "FY" | "DEFAULT"
        incomeBudget: number
        expenseBudget: number
        budgetRemaining: number
        projectedRevenue: number
        lastBilledPeriod: number | null
        lastBilledDays: number | null
        lastMonthRevenue: number
        averageMonthlyRevenue: number
    }
    series: ProjectHealthPeriod[]
    indicators: {
        startDate: string | null
        endDate: string | null
        elapsedPercent: number | null
        averageTaskCompletion: number | null
        isPastEndDate: boolean
        overdueTasks: number
        totalTasks: number
        totalHours: number
        totalBillableHours: number
        lastSyncAt: string | null
    }
}

// One frozen month (ProjectHealthSnapshot)
export interface ProjectHealthHistoryItem {
    fiscalPeriod: number
    status: HealthStatus
    failingChecks: FailingCheck[]
    computedAt: string
}

export interface PortfolioKpis {
    projectCount: number
    evaluatedProjects: number
    revenue: number
    cost: number
    accrued: number
    grossMarginPercent: number | null
    marginTarget: number
    marginMinimum: number
    marginWarning: number
    marginTargetFiscalYear: number
    arOver60Days: number | null
    statusCounts: Record<HealthStatus, number>
}

const FISCAL_START_MONTH_INDEX = 8 // September

export class ProjectHealthUI {
    static label(status: HealthStatus | null | undefined): string {
        switch (status) {
            case HealthStatus.GOOD: return "Good"
            case HealthStatus.WARNING: return "Warning"
            case HealthStatus.CRITICAL: return "Critical"
            case HealthStatus.NOT_EVALUATED: return "Not evaluated"
            default: return "—"
        }
    }

    static chipVariant(status: HealthStatus | null | undefined): ChipVariant {
        switch (status) {
            case HealthStatus.GOOD: return "success"
            case HealthStatus.WARNING: return "warning"
            case HealthStatus.CRITICAL: return "danger"
            default: return "secondary"
        }
    }

    // Bootstrap colour name, for borders and icons
    static color(status: HealthStatus | null | undefined): string {
        switch (status) {
            case HealthStatus.GOOD: return "success"
            case HealthStatus.WARNING: return "warning"
            case HealthStatus.CRITICAL: return "danger"
            default: return "secondary"
        }
    }

    static icon(status: HealthStatus | null | undefined): string {
        switch (status) {
            case HealthStatus.GOOD: return "mdi mdi-check-circle"
            case HealthStatus.WARNING: return "mdi mdi-alert"
            case HealthStatus.CRITICAL: return "mdi mdi-alert-octagon"
            default: return "mdi mdi-minus-circle-outline"
        }
    }

    static headline(status: HealthStatus | null | undefined, failing: number): string {
        switch (status) {
            case HealthStatus.GOOD: return "All evaluated checks are healthy"
            case HealthStatus.WARNING: return `${failing} check${failing === 1 ? "" : "s"} need attention`
            case HealthStatus.CRITICAL: return `${failing} check${failing === 1 ? "" : "s"} failing — action required`
            case HealthStatus.NOT_EVALUATED: return "Not enough data to evaluate this project"
            default: return "Health is only evaluated for Active projects"
        }
    }

    // Packed fiscal period (fiscalYear*100 + period, September start) → "Sep 2025".
    // Built in UTC so it never shifts a month in local time.
    static periodLabel(fiscalPeriod: number | null | undefined): string {
        if (!fiscalPeriod) return "—"
        const fiscalYear = Math.floor(fiscalPeriod / 100)
        const period = fiscalPeriod % 100
        const monthIndex = (FISCAL_START_MONTH_INDEX + period - 1) % 12
        const year = monthIndex >= FISCAL_START_MONTH_INDEX ? fiscalYear - 1 : fiscalYear
        return new Date(Date.UTC(year, monthIndex, 1)).toLocaleString("en-CA", { month: "short", year: "numeric", timeZone: "UTC" })
    }
}
